import { getApps, initializeApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  SAMLAuthProvider,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { IDENTITY_PLATFORM_CONFIG } from '../components/common/constants';
import { getShibbolethProvider, validateIdentityConfiguration } from '../components/common/helpers';

/*
 * The browser uses Identity Platform through Google's Firebase Auth SDK.
 * "Firebase" is the SDK package name; this application does not use Firebase
 * Hosting or require a separate Firebase project.
 */
let clientAuth;

/**
 * Return the single Auth client shared by the application.
 */
export const getClientAuth = () => {
  if (!clientAuth) {
    // Validate the identity configuration from environment variables before initializing the Auth client.
    validateIdentityConfiguration();
    const app = getApps()[0] ?? initializeApp(IDENTITY_PLATFORM_CONFIG);
    clientAuth = getAuth(app);
  }

  return clientAuth;
};

/**
 * Observe login, logout, and restored browser sessions.
 */
export const observeAuth = (callback) => onAuthStateChanged(getClientAuth(), callback);

/**
 * Start the service-provider-initiated Shibboleth flow in a popup.
 */
export const logIn = (providerId) => {
  const auth = getClientAuth();
  const selectedProvider = getShibbolethProvider(providerId);
  const provider = new SAMLAuthProvider(selectedProvider.providerId);
  return signInWithPopup(auth, provider);
};

export const logOut = () => signOut(getClientAuth());

/**
 * Return a current ID token for an authenticated API request.
 *
 * The SDK refreshes expired tokens automatically, so callers should request the
 * token immediately before each API call instead of storing it themselves.
 */
export const getIdentityToken = async () => {
  const user = getClientAuth().currentUser;
  if (!user) {
    throw new Error('Please sign in again.');
  }

  return user.getIdToken();
};
